import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Save, Send, Eye, Receipt, Users, ExternalLink } from "lucide-react";
import { toast } from "sonner";

const WEEKDAYS = [["1", "Lunedì"], ["2", "Martedì"], ["3", "Mercoledì"], ["4", "Giovedì"], ["5", "Venerdì"], ["6", "Sabato"], ["0", "Domenica"]];

export default function VisiteFunnelDialog({ open, onOpenChange }) {
  const [cfg, setCfg] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [suggested, setSuggested] = useState([]);
  const [typeLabels, setTypeLabels] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState("");
  const [testEmail, setTestEmail] = useState("");
  const [summary, setSummary] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [recipientsText, setRecipientsText] = useState("");
  const [ccText, setCcText] = useState("");

  useEffect(() => {
    if (!open) return;
    setLoading(true); setSummary(null);
    api.get("/visite-funnel-config").then((r) => {
      setCfg(r.data.config); setCanEdit(!!r.data.can_edit);
      setSuggested(r.data.suggested_recipients || []); setTypeLabels(r.data.type_labels || {});
      setRecipientsText((r.data.config.recipients || []).join("\n"));
      setCcText((r.data.config.cc || []).join(", "));
    }).catch(() => toast.error("Errore caricamento configurazione")).finally(() => setLoading(false));
  }, [open]);

  const upd = (patch) => setCfg((c) => ({ ...c, ...patch }));
  const asList = (s) => s.split(/[\n,;]+/).map((x) => x.trim()).filter((x) => x.includes("@"));

  const save = async () => {
    setSaving(true);
    try {
      const r = await api.put("/visite-funnel-config", { config: cfg });
      setCfg(r.data.config); toast.success("Funnel visite salvato");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const anteprima = async () => {
    setBusy("preview");
    try {
      const r = await api.post("/visite-funnel-config/anteprima", { period_days: cfg.period_days, period_mode: cfg.period_mode, types: cfg.types });
      setSummary({ periodo: r.data.periodo, visite: r.data.tot_visite, km: r.data.tot_km, commerciali: r.data.n_commerciali });
      setPreviewHtml(r.data.html || "<p style='font-family:sans-serif;padding:16px'>Nessun contenuto.</p>");
    } catch (e) { toast.error(e.response?.data?.detail || "Errore anteprima"); }
    finally { setBusy(""); }
  };

  const inviaProva = async () => {
    setBusy("test");
    try {
      const r = await api.post("/visite-funnel-config/invia-prova", { email: testEmail || undefined, period_days: cfg.period_days, period_mode: cfg.period_mode, types: cfg.types });
      toast.success(`Prova inviata a ${r.data.to} (${r.data.tot_visite} visite)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Errore invio prova"); }
    finally { setBusy(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="visite-funnel-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Receipt className="h-5 w-5 text-primary" /> Funnel visite commerciali</DialogTitle>
          <DialogDescription>Invia alla Direzione (+ CC) l'elenco delle visite svolte dai commerciali nel periodo, per il controllo dei rimborsi spese.</DialogDescription>
        </DialogHeader>

        {loading || !cfg ? (
          <div className="py-16 text-center"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between rounded-lg border border-input p-3">
              <div>
                <div className="font-semibold">Funnel attivo</div>
                <div className="text-xs text-muted-foreground">Quando attivo, il report parte automaticamente dalla produzione.</div>
              </div>
              <Switch checked={!!cfg.enabled} onCheckedChange={(v) => upd({ enabled: v })} disabled={!canEdit} data-testid="visite-funnel-enabled" />
            </div>

            <div>
              <div className="mb-1 flex items-center justify-between">
                <Label className="text-xs">Destinatari Direzione (uno per riga o separati da virgola)</Label>
                {suggested.length > 0 && (
                  <Button type="button" size="sm" variant="ghost" className="h-6 text-[11px]" data-testid="visite-funnel-fill-direzione"
                    onClick={() => { const merged = Array.from(new Set([...(cfg.recipients || []), ...suggested])); upd({ recipients: merged }); setRecipientsText(merged.join("\n")); }}>
                    <Users className="mr-1 h-3 w-3" /> Aggiungi direzione
                  </Button>
                )}
              </div>
              <textarea className="w-full rounded-md border border-input bg-background p-2 text-sm" rows={2} data-testid="visite-funnel-recipients"
                value={recipientsText} onChange={(e) => { setRecipientsText(e.target.value); upd({ recipients: asList(e.target.value) }); }} placeholder="direzione@torrisolare.it" />
            </div>
            <div>
              <Label className="text-xs">In copia (CC)</Label>
              <Input value={ccText} data-testid="visite-funnel-cc" onChange={(e) => { setCcText(e.target.value); upd({ cc: asList(e.target.value) }); }} placeholder="cc1@..., cc2@..." className="h-9" />
            </div>

            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              <div>
                <Label className="text-xs">Frequenza</Label>
                <Select value={cfg.frequency} onValueChange={(v) => upd({ frequency: v })}>
                  <SelectTrigger data-testid="visite-funnel-frequency" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="daily">Giornaliera</SelectItem><SelectItem value="weekly">Settimanale</SelectItem><SelectItem value="biweekly">Bisettimanale (ogni 2 settimane)</SelectItem><SelectItem value="monthly">Mensile (1° del mese)</SelectItem></SelectContent>
                </Select>
              </div>
              {(cfg.frequency === "weekly" || cfg.frequency === "biweekly") && (
                <div>
                  <Label className="text-xs">Giorno</Label>
                  <Select value={String(cfg.weekday)} onValueChange={(v) => upd({ weekday: Number(v) })}>
                    <SelectTrigger data-testid="visite-funnel-weekday" className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>{WEEKDAYS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label className="text-xs">Orario</Label>
                <Input type="time" value={cfg.send_time} data-testid="visite-funnel-time" onChange={(e) => upd({ send_time: e.target.value })} className="h-9" />
              </div>
              {cfg.frequency === "monthly" && (
                <div className="flex items-center justify-between rounded-md border border-input p-2 sm:col-span-2">
                  <div>
                    <Label className="text-xs">Promemoria il 3 del mese</Label>
                    <p className="text-[11px] text-muted-foreground">Secondo invio di sollecito, stesso report del mese precedente.</p>
                  </div>
                  <Switch checked={cfg.reminder_enabled !== false} onCheckedChange={(v) => upd({ reminder_enabled: v })} data-testid="visite-funnel-reminder" />
                </div>
              )}
              <div>
                <Label className="text-xs">Periodo report</Label>
                <Select value={cfg.period_mode || "mese"} onValueChange={(v) => upd({ period_mode: v })}>
                  <SelectTrigger data-testid="visite-funnel-period-mode" className="h-9"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="mese">Mese in corso</SelectItem><SelectItem value="mese_scorso">Mese precedente</SelectItem><SelectItem value="giorni">Ultimi N giorni</SelectItem></SelectContent>
                </Select>
                {cfg.period_mode === "giorni" && (
                  <div className="mt-1.5">
                    <Input type="number" min={1} value={cfg.period_days} data-testid="visite-funnel-period" onChange={(e) => upd({ period_days: Number(e.target.value) })} className="h-9" placeholder="giorni" />
                    <div className="mt-1 flex flex-wrap gap-1">
                      {[["Oggi", 1], ["7 giorni", 7], ["2 settimane", 14], ["30 giorni", 30]].map(([l, v]) => (
                        <button key={v} type="button" onClick={() => upd({ period_days: v })} data-testid={`visite-funnel-period-${v}`}
                          className={`rounded border px-2 py-0.5 text-[11px] transition-colors ${Number(cfg.period_days) === v ? "border-primary bg-primary/15 text-foreground" : "border-input text-muted-foreground hover:bg-accent"}`}>{l}</button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div>
              <Label className="text-xs">Tipi di attività da includere</Label>
              <div className="mt-1 flex flex-wrap gap-2">
                {Object.entries(typeLabels).map(([k, l]) => {
                  const on = (cfg.types || []).includes(k);
                  return (
                    <button key={k} type="button" data-testid={`visite-funnel-type-${k}`}
                      onClick={() => upd({ types: on ? cfg.types.filter((t) => t !== k) : [...(cfg.types || []), k] })}
                      className={`rounded-full border px-3 py-1 text-xs transition-colors ${on ? "border-primary bg-primary/15 text-foreground" : "border-input text-muted-foreground hover:bg-accent"}`}>
                      {l}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <Label className="text-xs">Oggetto email</Label>
              <Input value={cfg.subject} data-testid="visite-funnel-subject" onChange={(e) => upd({ subject: e.target.value })} className="h-9" />
              <p className="mt-0.5 text-[11px] text-muted-foreground">Usa <code>{"{periodo}"}</code> per inserire l'intervallo di date.</p>
            </div>

            {summary && (
              <div className="rounded-md border border-primary/40 bg-primary/5 p-3 text-sm" data-testid="visite-funnel-summary">
                Periodo <b>{summary.periodo}</b> · <b>{summary.visite}</b> visite · <b>{summary.commerciali}</b> commerciali · <b>{summary.km}</b> km
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 border-t border-input pt-3">
              <Button type="button" variant="outline" size="sm" onClick={() => anteprima()} disabled={busy === "preview"} data-testid="visite-funnel-preview">
                {busy === "preview" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Eye className="mr-1.5 h-4 w-4" />} Anteprima
              </Button>
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="email per la prova" className="h-9 w-52" data-testid="visite-funnel-test-email" />
              <Button type="button" variant="outline" size="sm" onClick={inviaProva} disabled={busy === "test"} data-testid="visite-funnel-send-test">
                {busy === "test" ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Send className="mr-1.5 h-4 w-4" />} Invia prova
              </Button>
              {canEdit && (
                <Button type="button" className="ml-auto" onClick={save} disabled={saving} data-testid="visite-funnel-save">
                  {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Salva
                </Button>
              )}
            </div>
            {!canEdit && <p className="text-[11px] text-amber-600 dark:text-amber-300">Sola lettura: solo Direzione/Back Office può modificare e salvare.</p>}
          </div>
        )}
      </DialogContent>

      <Dialog open={!!previewHtml} onOpenChange={(o) => { if (!o) setPreviewHtml(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-hidden p-0" data-testid="visite-funnel-preview-dialog">
          <DialogHeader className="border-b border-border p-4">
            <DialogTitle className="flex items-center gap-2"><Eye className="h-5 w-5 text-primary" /> Anteprima email — Funnel visite</DialogTitle>
            <DialogDescription>{summary ? `Periodo ${summary.periodo} · ${summary.visite} visite · ${summary.km} km` : "Anteprima del report"}</DialogDescription>
          </DialogHeader>
          <iframe title="Anteprima Funnel visite" srcDoc={previewHtml || ""} data-testid="visite-funnel-preview-iframe"
            className="h-[70vh] w-full border-0 bg-white" sandbox="" />
        </DialogContent>
      </Dialog>
    </Dialog>
  );
}
